import React, { useEffect, useState, useMemo } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import DataTable from "react-data-table-component";
import Select from "react-select"; // ✅ React Select
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

function EmergencyReports() {
  // helper date/format functions
  function parseServerDate(datetimeStr) {
    if (!datetimeStr) return null;
    const cleaned = String(datetimeStr).replace("T", " ").trim();
    const [datePart, timePart] = cleaned.split(" ");
    const [year, month, day] = (datePart || "").split("-").map((n) => parseInt(n, 10));
    if (!year || !month || !day) return null;
    let hours = 0, minutes = 0, seconds = 0;
    if (timePart) {
      const parts = timePart.split(":").map((n) => parseInt(n, 10));
      hours = Number.isFinite(parts[0]) ? parts[0] : 0;
      minutes = Number.isFinite(parts[1]) ? parts[1] : 0;
      seconds = Number.isFinite(parts[2]) ? parts[2] : 0;
    }
    return new Date(year, month - 1, day, hours, minutes, seconds);
  }

  function formatDateTime12h(dateObj) {
    if (!dateObj) return "";
    const d = dateObj;
    const day = String(d.getDate()).padStart(2, "0");
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day} ${month} - ${year} ${String(hours).padStart(2,"0")}:${minutes} ${ampm}`;
  }

  function classifyMode(raw) {
    if (!raw) return "";
    const s = String(raw).toLowerCase();
    if (s.includes("cash")) return "cash";
    if (s.includes("card") || s.includes("credit") || s.includes("debit") || s.includes("swipe")) return "card";
    if (s.includes("online") || s.includes("upi") || s.includes("gateway") || s.includes("net")) return "online";
    return "";
  }

  function mapPayMode(raw) {
    if (!raw) return "";
    const s = String(raw).trim().toLowerCase();
    if (s.includes("cash")) return "Cash";
    if (s.includes("card") || s.includes("credit") || s.includes("debit") || s.includes("swipe")) return "Card";
    if (s.includes("online") || s.includes("upi") || s.includes("gateway") || s.includes("net")) return "Online";
    return raw;
  }

  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  })();

  // state
  const [formData, setFormData] = useState({
    l1: [],
    docs: [],
    users: [],
    cats: [],
    cases: [],
    from: today,
    to: today,
    search_key: "",
    selectedDoctorId: "",
    selectedUserId: ""
  });

  const [masterList, setMasterList] = useState([]);
  const [doctorsMap, setDoctorsMap] = useState({});
  const [summary, setSummary] = useState({ 
    totalReports: 0, 
    totalAmount: 0, 
    todaysCollection: 0, 
    collectionBy: { cash:0, card:0, online:0 } 
  });

  const [totals, setTotals] = useState({
    totalAmount: 0,
    totalRegCharges: 0,
    totalProcedureBills: 0,
    paidAmount: 0,
    dueAmount: 0,
    totalRows: 0,
    cashTotal: 0,
    cardTotal: 0,
    onlineTotal: 0,
    procedureCash: 0,
    procedureCard: 0,
    procedureOnline: 0
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  // Lookups
  function getDoctorNameById(id, fallbackName) {
    if (fallbackName && String(fallbackName).trim() !== "") return String(fallbackName).trim();
    if (id !== undefined && id !== null && String(id).trim() !== "") {
      const key = String(id).trim();
      if (doctorsMap && typeof doctorsMap === 'object') {
        if (doctorsMap[key]) return doctorsMap[key];
        if (!isNaN(Number(key)) && doctorsMap[String(Number(key))]) return doctorsMap[String(Number(key))];
      }
    }
    const docsArray = Array.isArray(formData.docs) ? formData.docs : [];
    if (id !== undefined && id !== null && String(id).trim() !== "") {
      const strId = String(id).trim();
      const doctorById = docsArray.find(d => {
        if (!d) return false;
        const candidates = [
          d.user_id, d.id, d.userId, d.emp_id, d.uid, d.doctor_id,
          d.doc?.doc_parent, d.doc?.doc_user, d.doc?.doc_id
        ].filter(Boolean);
        return candidates.some(c => String(c).trim() === strId);
      });
      if (doctorById) {
        const name = (doctorById.fname || doctorById.name || doctorById.full_name || '').trim();
        const degree = (doctorById.doc && (doctorById.doc.doc_degree || doctorById.doc.doc_specality)) || '';
        return degree ? `${name} (${degree})` : (name || `Doctor #${strId}`);
      }
    }
    if (id && isNaN(Number(String(id).trim()))) return String(id).trim();
    if (fallbackName) return fallbackName;
    if (id !== undefined && id !== null && String(id).trim() !== "") return `Doctor #${String(id).trim()}`;
    return 'N/A';
  }

  // Fetch doctors
  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        const docs = res.data.records;
        setFormData(prev => ({ ...prev, docs }));

        const map = {};
        docs.forEach(d => {
          const display = (d.fname || d.name || d.full_name || (d.doc && (d.doc.doc_name || '')) || d.username || "").toString().trim();
          const displayWithExtra = (() => {
            const deg = d.doc?.doc_degree ?? d.doc?.doc_specality ?? "";
            return deg ? (display ? `${display} (${deg})` : String(display)) : (display || "");
          })();

          const keys = [
            d.user_id, d.id, d.userId, d.emp_id, d.uid, d.doctor_id,
            d.doc?.doc_parent, d.doc?.doc_user, d.doc?.doc_id
          ];

          keys.forEach(k => {
            if (k !== undefined && k !== null && String(k).trim() !== "") {
              map[String(k).trim()] = displayWithExtra || display || String(k);
            }
          });

          if (display) map[display.toLowerCase()] = displayWithExtra || display;
        });

        setDoctorsMap(map);
      }
    } catch (err) {
      console.warn("fetchDoctors", err);
    }
  };

  // Main fetch
  const initData = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none");
      if (formData.search_key) fd.append("search_key", formData.search_key);
      if (formData.from) {
        fd.append("from", `${formData.from} 00:00:00`); // Start of day
      }
      if (formData.to) {
        fd.append("to", `${formData.to} 23:59:59`); // End of day
      }
      if (formData.selectedDoctorId) fd.append("doctor_id", String(formData.selectedDoctorId));
      if (formData.selectedUserId) fd.append("user_id", String(formData.selectedUserId));

      const resp = await axios.post(AppConst.BASE_URL + AppConst.getEmergencyListUrl, fd, { 
        headers: { 'Content-Type':'multipart/form-data' }
      });

      if (resp?.data?.status && Array.isArray(resp.data.records)) {
        const records = resp.data.records;

        // Normalize numeric fields
        records.forEach(r => {
          r.ipd_reg_charges = Number(r.ipd_reg_charges ?? 0);
          r.ipd_paid = Number(r.ipd_paid ?? 0);
          r.ipd_due = Number(r.ipd_due ?? 0);
          r.procedure_bill_amount = Number(r.procedure_bill_amount ?? 0);
          r.procedure_bill_cash = Number(r.procedure_bill_cash ?? 0);
          r.procedure_bill_card = Number(r.procedure_bill_card ?? 0);
          r.procedure_bill_online = Number(r.procedure_bill_online ?? 0);
          r.total_amount = Number(r.total_amount ?? (r.ipd_reg_charges + r.procedure_bill_amount));
        });

        setMasterList(records);

        // Merge doctor hints
        setDoctorsMap(prev => {
          const copy = { ...(prev || {}) };
          records.forEach(r => {
            const idCandidates = [
              r.ipd_refer_doctor, r.doctor_id, r.ipd_doc_id
            ].filter(x => x !== undefined && x !== null && String(x).trim() !== "");

            const nameCandidates = [
              r.ipd_refer_doctor_name, r.doctor_name, r.refer_doctor
            ].filter(Boolean).map(x => String(x).trim()).filter(x=>x!=="");

            if (nameCandidates.length > 0) {
              const pick = nameCandidates[0];
              idCandidates.forEach(k => { copy[String(k).trim()] = pick; });
              copy[pick.toLowerCase()] = pick;
            }
          });
          return copy;
        });

        // Summary
        let totalAmount = 0;
        let todaysCollection = 0;
        let cashToday = 0, cardToday = 0, onlineToday = 0;

        const localToday = today;
        records.forEach(r => {
          totalAmount += Number(r.total_amount ?? 0);
          
          const regDt = parseServerDate(r.ipd_reg_date ?? r.ipd_datetime ?? "");
          const regYMD = regDt ? `${regDt.getFullYear()}-${String(regDt.getMonth()+1).padStart(2,'0')}-${String(regDt.getDate()).padStart(2,'0')}` : "";
          
          if (regYMD === localToday) {
            const paid = Number(r.ipd_paid ?? 0);
            const procPaid = Number(r.procedure_bill_amount ?? 0);
            const totalPaidToday = paid + procPaid;
            todaysCollection += totalPaidToday;

            // Registration payment mode
            const pm = classifyMode(r.ipd_pay_mode ?? "");
            if (pm === "cash") cashToday += paid;
            else if (pm === "card") cardToday += paid;
            else if (pm === "online") onlineToday += paid;
            else cashToday += paid;

            // Procedure bill payment modes
            cashToday += Number(r.procedure_bill_cash ?? 0);
            cardToday += Number(r.procedure_bill_card ?? 0);
            onlineToday += Number(r.procedure_bill_online ?? 0);
          }
        });

        setSummary({
          totalReports: records.length,
          totalAmount,
          todaysCollection,
          collectionBy: { cash: cashToday, card: cardToday, online: onlineToday }
        });

      } else {
        setMasterList([]);
        setSummary({ totalReports: 0, totalAmount: 0, todaysCollection: 0, collectionBy:{cash:0,card:0,online:0}});
        if (resp?.data && resp.data.message) toast.info(resp.data.message);
      }
    } catch (err) {
      console.error("initData error:", err);
      toast.error("Failed to fetch Emergency data.");
    }
  };

  useEffect(() => {
    fetchDoctors();
    initData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => initData(), 160);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [formData.from, formData.to, formData.search_key, formData.selectedDoctorId, formData.selectedUserId]);

  // Group by UHID
  const groupByUHID = (records) => {
    const grouped = {};
    records.forEach(r => {
      const uhid = r.ipd_uhid || `UHID_${r.ipd_id || Math.random()}`;
      const paid = Number(r.ipd_paid ?? 0);
      const regCharge = Number(r.ipd_reg_charges ?? 0);
      const procAmount = Number(r.procedure_bill_amount ?? 0);
      const totalAmt = Number(r.total_amount ?? 0);

      if (!grouped[uhid]) {
        grouped[uhid] = {
          ipd_id: r.ipd_id,
          ipd_uhid: r.ipd_uhid,
          ipd_patient_name: r.ipd_patient_name,
          ipd_patient_mobile: r.ipd_patient_mobile,
          ipd_refer_doctor: r.ipd_refer_doctor,
          ipd_refer_doctor_name: r.ipd_refer_doctor_name,
          total_amount: totalAmt,
          total_reg_charges: regCharge,
          total_procedure_bills: procAmount,
          total_paid: paid + procAmount,
          total_due: Number(r.ipd_due ?? 0),
          payment_mode_sample: r.ipd_pay_mode ?? '',
          procedure_bill_cash: Number(r.procedure_bill_cash ?? 0),
          procedure_bill_card: Number(r.procedure_bill_card ?? 0),
          procedure_bill_online: Number(r.procedure_bill_online ?? 0),
          user_display: r.user_display || r.last_used || r.fname || '',
          user_id_sample: r.last_used_id ?? r.user_id ?? r.ipd_user ?? '',
          ipd_reg_date: r.ipd_reg_date || r.ipd_datetime || '',
          __firstRow: r
        };
      } else {
        grouped[uhid].total_amount += totalAmt;
        grouped[uhid].total_reg_charges += regCharge;
        grouped[uhid].total_procedure_bills += procAmount;
        grouped[uhid].total_paid += (paid + procAmount);
        grouped[uhid].total_due = Math.max(0, grouped[uhid].total_due + Number(r.ipd_due ?? 0));
        grouped[uhid].procedure_bill_cash += Number(r.procedure_bill_cash ?? 0);
        grouped[uhid].procedure_bill_card += Number(r.procedure_bill_card ?? 0);
        grouped[uhid].procedure_bill_online += Number(r.procedure_bill_online ?? 0);
      }
    });
    return Object.values(grouped);
  };

  // Filtered list
  const filteredList = useMemo(() => {
    let list = [...masterList];

    if (formData.selectedDoctorId) {
      list = list.filter(r => String(r.ipd_refer_doctor || "") === String(formData.selectedDoctorId));
    }

    if (formData.selectedUserId && String(formData.selectedUserId).trim() !== "") {
      const su = String(formData.selectedUserId);
      list = list.filter(r => {
        const uidCandidates = [r.last_used_id, r.user_id, r.ipd_user].map(x => x ? String(x) : "");
        return uidCandidates.includes(su);
      });
    }

    const grouped = groupByUHID(list);

    if (formData.search_key && formData.search_key.trim() !== "") {
      const sk = formData.search_key.toLowerCase();
      return grouped.filter(g =>
        (`${g.ipd_uhid || ''}`).toLowerCase().includes(sk) ||
        (`${g.ipd_patient_name || ''}`).toLowerCase().includes(sk) ||
        (`${g.ipd_patient_mobile || ''}`).toLowerCase().includes(sk)
      );
    }
    return grouped;
  }, [masterList, formData.search_key, formData.selectedDoctorId, formData.selectedUserId]);

  // ✅ Derive users list from current patient list (filtered)
  const usersListFromPatients = useMemo(() => {
    const userMap = {};
    
    masterList.forEach(r => {
      const uid = r.last_used_id ?? r.user_id ?? r.ipd_user ?? null;
      const uname = r.user_display || r.last_used || r.fname || r.username || null;
      
      if (uid && uname) {
        userMap[String(uid)] = uname;
      }
    });

    return Object.keys(userMap)
      .map(uid => ({ user_id: uid, name: userMap[uid] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [masterList]);

  // ✅ Doctor options for React Select
  const doctorOptions = useMemo(() => {
    const options = Object.entries(doctorsMap)
      .filter(([k, v]) => k && String(k).trim() !== "" && String(v).trim() !== "")
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([key, label]) => ({
        value: key,
        label: label
      }));

    return [{ value: "", label: "All Doctors" }, ...options];
  }, [doctorsMap]);

  // Totals effect
  useEffect(() => {
    let cash = 0, card = 0, online = 0;
    let regCharges = 0, procBills = 0, paid = 0, due = 0;
    let procCash = 0, procCard = 0, procOnline = 0;

    filteredList.forEach(row => {
      regCharges += Number(row.total_reg_charges ?? 0);
      procBills += Number(row.total_procedure_bills ?? 0);
      paid += Number(row.total_paid || 0);
      due += Number(row.total_due || 0);

      // Registration payment mode
      const mode = classifyMode(row.payment_mode_sample || '');
      const regPaid = Number(row.__firstRow?.ipd_paid || 0);
      if (mode === "cash") cash += regPaid;
      else if (mode === "card") card += regPaid;
      else if (mode === "online") online += regPaid;

      // Procedure bill payment modes
      procCash += Number(row.procedure_bill_cash ?? 0);
      procCard += Number(row.procedure_bill_card ?? 0);
      procOnline += Number(row.procedure_bill_online ?? 0);

      cash += procCash;
      card += procCard;
      online += procOnline;
    });

    setTotals(prev => ({
      ...prev,
      totalAmount: Number((regCharges + procBills).toFixed(2)),
      totalRegCharges: Number(regCharges.toFixed(2)),
      totalProcedureBills: Number(procBills.toFixed(2)),
      paidAmount: Number(paid.toFixed(2)),
      dueAmount: Number(due.toFixed(2)),
      cashTotal: Number(cash.toFixed(2)),
      cardTotal: Number(card.toFixed(2)),
      onlineTotal: Number(online.toFixed(2)),
      procedureCash: Number(procCash.toFixed(2)),
      procedureCard: Number(procCard.toFixed(2)),
      procedureOnline: Number(procOnline.toFixed(2)),
      totalRows: filteredList.length
    }));
  }, [filteredList]);

  // DataTable columns
  const columns = useMemo(() => [
    { name: "#", selector: (row,i) => i+1, width: "60px" },
    { name: "UHID", selector: row => row.ipd_uhid || "", sortable: true, width: '180px' },
    { 
      name: "Patient", 
      selector: row => `${row.ipd_patient_name || ''}`, 
      cell: row => (
        <div>
          {row.ipd_patient_name}<br/>
          <small className="text-muted">{row.ipd_patient_mobile}</small>
        </div>
      ), 
      wrap:true, 
      width: '200px'
    },
    { 
      name: 'Doctor',
      selector: row => row.ipd_refer_doctor_name || getDoctorNameById(row.ipd_refer_doctor),
      sortable: true,
      wrap: true,
      width: '200px'
    },
    {
      name: "User",
      selector: row => row.user_display || "",
      width: "160px",
      cell: row => {
        const uname = row.user_display || "";
        return <div>{uname}</div>;
      }
    },
    { name: "Reg Charges", selector: row => Number(row.total_reg_charges||0).toFixed(2), sortable:true, right:true, width: '120px' },
    { name: "Procedure Bill", selector: row => Number(row.total_procedure_bills||0).toFixed(2), sortable:true, right:true, width: '130px' },
    { name: "Total", selector: row => Number(row.total_amount||0).toFixed(2), sortable:true, right:true, width: '110px' },
    { name: "Paid", selector: row => Number(row.total_paid||0).toFixed(2), sortable:true, right:true, width: '110px' },
    { name: "Due", selector: row => Number(row.total_due||0).toFixed(2), sortable:true, right:true, width: '110px' },
    { name: "Payment Mode", selector: row => mapPayMode(row.payment_mode_sample || ""), width: "130px" },
    {
      name: "Date",
      selector: row => { 
        const dt = parseServerDate(row.ipd_reg_date || row.__firstRow?.ipd_reg_date || ""); 
        return dt ? dt.getTime() : 0; 
      },
      sortable:true,
      cell: row => { 
        const dt = parseServerDate(row.ipd_reg_date || row.__firstRow?.ipd_reg_date || ""); 
        return dt ? formatDateTime12h(dt) : ""; 
      },
      width: "230px"
    }
  ], [doctorsMap]);

  // Export PDF
  function generatePrintHtml(list, totalsForFooter) {
    const style = `
      <style>
        body{font-family: Arial, Helvetica, sans-serif; padding: 20px; color: #222;}
        table{border-collapse: collapse; width:100%; margin-top:10px;}
        th, td{border:1px solid #ccc; padding:6px 8px; text-align:left; font-size:12px;}
        th{background:#f2f2f2;}
        .footer-summary{margin-top:12px; font-size:13px;}
      </style>
    `;

    const rowsHtml = list.map((r,i) => {
      const docName = r.ipd_refer_doctor_name || getDoctorNameById(r.ipd_refer_doctor) || "";
      const uname = r.user_display || "";
      return `
        <tr>
          <td>${i+1}</td>
          <td>${r.ipd_uhid||''}</td>
          <td>${r.ipd_patient_name||''}</td>
          <td>${docName}</td>
          <td>${uname}</td>
          <td style="text-align:right">${Number(r.total_reg_charges||0).toFixed(2)}</td>
          <td style="text-align:right">${Number(r.total_procedure_bills||0).toFixed(2)}</td>
          <td style="text-align:right">${Number(r.total_amount||0).toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const footerHtml = `
      <div class="footer-summary">
        <div><strong>Total Patients:</strong> ${totalsForFooter.totalRows}</div>
        <div><strong>Total Reg Charges:</strong> ₹${totalsForFooter.totalRegCharges}</div>
        <div><strong>Total Procedure Bills:</strong> ₹${totalsForFooter.totalProcedureBills}</div>
        <div><strong>Total Amount:</strong> ₹${totalsForFooter.totalAmount}</div>
        <div><strong>Cash Paid:</strong> ₹${totalsForFooter.cashTotal} (Proc: ₹${totalsForFooter.procedureCash})</div>
        <div><strong>Card Paid:</strong> ₹${totalsForFooter.cardTotal} (Proc: ₹${totalsForFooter.procedureCard})</div>
        <div><strong>Online Paid:</strong> ₹${totalsForFooter.onlineTotal} (Proc: ₹${totalsForFooter.procedureOnline})</div>
        <div><strong>Paid Total:</strong> ₹${totalsForFooter.paidAmount}</div>
        <div><strong>Due Total:</strong> ₹${totalsForFooter.dueAmount}</div>
      </div>
    `;

    return `
      <!doctype html>
      <html>
        <head><meta charset="utf-8" /><title>Emergency Reports Print</title>${style}</head>
        <body>
          <h2>Emergency / DayCare Reports</h2>
          <div>Generated on: ${new Date().toLocaleString()}</div>

          <h3 style="margin-top:14px;">Registrations</h3>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>UHID</th>
                <th>Name</th>
                <th>Doctor</th>
                <th>User</th>
                <th>Reg Charges</th>
                <th>Procedure Bill</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>

          ${footerHtml}
        </body>
      </html>
    `;
  }

  function handleExportPDF() {
    const rows = filteredList || [];
    const footerTotals = {
      totalRows: totals.totalRows,
      totalAmount: totals.totalAmount,
      totalRegCharges: totals.totalRegCharges || 0,
      totalProcedureBills: totals.totalProcedureBills || 0,
      cashTotal: totals.cashTotal || 0,
      cardTotal: totals.cardTotal || 0,
      onlineTotal: totals.onlineTotal || 0,
      procedureCash: totals.procedureCash || 0,
      procedureCard: totals.procedureCard || 0,
      procedureOnline: totals.procedureOnline || 0,
      paidAmount: totals.paidAmount || 0,
      dueAmount: totals.dueAmount || 0
    };

    const html = generatePrintHtml(rows, footerTotals);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document || iframe.contentDocument || iframe.document;
    if (!doc) { 
      toast.error("Unable to create print frame."); 
      document.body.removeChild(iframe); 
      return; 
    }

    doc.open(); 
    doc.write(html); 
    doc.close();

    iframe.onload = () => {
      try { 
        iframe.contentWindow.focus(); 
        setTimeout(()=>{ 
          try{ 
            iframe.contentWindow.print(); 
          } catch(e){ 
            toast.error("Print failed"); 
          } finally { 
            setTimeout(()=>{ 
              try{ 
                document.body.removeChild(iframe); 
              }catch(e){} 
            },500); 
          } 
        },300); 
      } catch(e){ 
        console.error(e); 
        toast.error("Print failed"); 
        try{ 
          document.body.removeChild(iframe);
        }catch(_){} 
      }
    };

    setTimeout(() => {
      if (document.body.contains(iframe)) {
        try { 
          iframe.contentWindow.focus(); 
          iframe.contentWindow.print(); 
        } catch(e){} finally { 
          setTimeout(()=>{ 
            try{ 
              document.body.removeChild(iframe);
            }catch(_){} 
          },500); 
        }
      }
    }, 800);
  }

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#f1f5fb',
        color: '#405189',
        fontSize: '15px',
        fontWeight: '700',
        paddingLeft: '14px',
        paddingRight: '14px',
      }
    },
    rows: {
      style: {
        fontSize: '16px',
        minHeight: '58px',
      },
    },
    cells: {
      style: {
        paddingLeft: '14px',
        paddingRight: '14px',
        fontSize: '15px',
        lineHeight: '55px'
      }
    },
  };

  // ✅ React Select custom styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#ced4da',
      '&:hover': { borderColor: '#86b7fe' }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  // UI
  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Summary cards */}
        <div className="row mb-6">
          <div className="col-md-6">
            <div className="card p-3">
              <p className="mb-1 text-muted">
                <div
                  className="bg-light-success rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: 50, height: 50 }}
                >
                  <i className="bi bi-file-earmark-medical" style={{ fontSize: 26, color: "#198754" }} />
                </div>
                Total Patients
              </p>
              <h4 className="mb-0" style={{ color: "#14532d" }}>{summary.totalReports}</h4>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card p-3">
              <p className="mb-1 text-muted">
                <div
                  className="bg-light-success rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: 50, height: 50 }}
                >
                  <i className="bi bi-currency-rupee" style={{ fontSize: 26, color: "#198754" }} />
                </div>
                Total Amount
              </p>
              <h4 className="mb-0" style={{ color: "#14532d" }}>{Number(summary.totalAmount||0).toFixed(2)}</h4>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search (UHID / Name / Mobile)</label>
                <input 
                  className="form-control" 
                  name="search_key" 
                  value={formData.search_key} 
                  onChange={handleChange} 
                  placeholder="Search by name, UHID"
                />
              </div>
              <div className="col-md-2">
                <label>From</label>
                <input 
                  className="form-control" 
                  type="date" 
                  name="from" 
                  value={formData.from} 
                  onChange={handleChange} 
                />
              </div>
              <div className="col-md-2">
                <label>To</label>
                <input 
                  className="form-control" 
                  type="date" 
                  name="to" 
                  value={formData.to} 
                  onChange={handleChange} 
                />
              </div>

              {/* ✅ React Select for Doctor */}
              <div className="col-md-3">
                <label>Doctor</label>
                <Select
                  options={doctorOptions}
                  value={doctorOptions.find(opt => opt.value === formData.selectedDoctorId) || doctorOptions[0]}
                  onChange={(selected) => {
                    setFormData(prev => ({ ...prev, selectedDoctorId: selected?.value || "" }));
                  }}
                  styles={selectStyles}
                  placeholder="Select Doctor"
                  isClearable={false}
                  isSearchable={true}
                />
              </div>

              {/* ✅ User dropdown - only from patient list */}
              <div className="col-md-2">
                <label>User</label>
                <select 
                  className="form-control" 
                  value={formData.selectedUserId || "all"} 
                  onChange={(e) => {
                    const val = e.target.value === "all" ? "" : e.target.value;
                    setFormData(prev => ({ ...prev, selectedUserId: val }));
                  }}
                >
                  <option value="all">All Users</option>
                  {usersListFromPatients.map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row g-2 mt-2">
              <div className="col-md-2">
                <button 
                  className="btn btn-secondary w-100" 
                  onClick={()=>{ 
                    setFormData(prev=>({
                      ...prev, 
                      search_key:'', 
                      from: today, 
                      to: today, 
                      selectedDoctorId: '', 
                      selectedUserId: ''
                    })); 
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table & export */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between mb-2">
              <div/> 
              <div>
                <button className="btn btn-outline-primary" onClick={handleExportPDF}>
                  Export / Print
                </button>
              </div>
            </div>

            <DataTable 
              columns={columns} 
              data={filteredList} 
              customStyles={customStyles} 
              pagination 
              highlightOnHover 
              dense 
              persistTableHead 
              responsive 
            />

            <div className="d-flex justify-content-end mt-3">
              <div className="text-muted">
                <strong>Total Reg Charges:</strong> ₹{totals.totalRegCharges} &nbsp;|&nbsp;
                <strong>Total Procedure Bills:</strong> ₹{totals.totalProcedureBills} &nbsp;|&nbsp;
                <strong>Total Amount:</strong> ₹{totals.totalAmount} &nbsp;|&nbsp;
                <strong>Cash:</strong> ₹{totals.cashTotal} (Proc: ₹{totals.procedureCash}) &nbsp;|&nbsp;
                <strong>Card:</strong> ₹{totals.cardTotal} (Proc: ₹{totals.procedureCard}) &nbsp;|&nbsp;
                <strong>Online:</strong> ₹{totals.onlineTotal} (Proc: ₹{totals.procedureOnline}) &nbsp;|&nbsp;
                <strong>Paid:</strong> ₹{totals.paidAmount} &nbsp;|&nbsp;
                <strong>Due:</strong> ₹{totals.dueAmount} &nbsp;|&nbsp;
                <strong>Total Patients:</strong> {totals.totalRows}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmergencyReports;